import { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { Activity, Database, Clock, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { zhCN } from 'date-fns/locale';

function Dashboard() {
  const [config, setConfig] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const storedConfig = JSON.parse(localStorage.getItem('qianshu_config') || '{}');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [configData, versionsData] = await Promise.all([
        apiClient.getConfig(storedConfig.appId),
        apiClient.getVersions(storedConfig.appId, 5, 0),
      ]);

      setConfig(configData.data);
      setVersions(versionsData.data.versions);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="flex items-center text-red-600">
          <AlertCircle className="w-5 h-5 mr-2" />
          <span>加载失败: {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 统计卡片 */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Activity className="h-8 w-8 text-primary-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">当前版本</p>
              <p className="text-2xl font-semibold text-gray-900">v{config?.version || 0}</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Database className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">配置项数</p>
              <p className="text-2xl font-semibold text-gray-900">
                {config?.config ? Object.keys(config.config).length : 0}
              </p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">最后更新</p>
              <p className="text-sm font-semibold text-gray-900">
                {config?.created_at 
                  ? formatDistanceToNow(new Date(config.created_at * 1000), { 
                      addSuffix: true, 
                      locale: zhCN 
                    })
                  : '未知'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 当前配置 */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">当前配置</h2>
        {config?.comment && (
          <p className="text-sm text-gray-600 mb-4">📝 {config.comment}</p>
        )}
        <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-sm">
          {config?.config ? JSON.stringify(config.config, null, 2) : '暂无配置'}
        </pre>
      </div>

      {/* 最近版本 */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">最近版本</h2>
        <div className="space-y-3">
          {versions.map((version) => (
            <div
              key={version.version}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div>
                <div className="flex items-center">
                  <span className="font-medium text-gray-900">版本 {version.version}</span>
                  {version.is_active === 1 && (
                    <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                      当前
                    </span>
                  )}
                </div>
                {version.comment && (
                  <p className="text-sm text-gray-600 mt-1">{version.comment}</p>
                )}
              </div>
              <div className="text-sm text-gray-500">
                {formatDistanceToNow(new Date(version.created_at * 1000), { 
                  addSuffix: true, 
                  locale: zhCN 
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
